/* ══ v14.3 (b): MOVE A PROJECT BETWEEN PROGRAMMES ══════════════════════════════════════════
   ⚠ ONE FIELD EDIT ON ONE RECORD, and that is v14's doing rather than cleverness here: a
   project is a FLAT record carrying a single `programmeId`, so programme membership is a
   pointer, not a structure. Every per-project register rides along untouched because none of
   them stores a programme, and navigation already follows the project — v14.1 (b) made Focus,
   the switcher and the pickers derive the programme FROM it. Concurrently this is an ordinary
   project edit under the standing merge rules: no new conflict class. */
let _mvProjectId = null;

function openMoveProject(id, e) {
  if (e) e.stopPropagation();
  _closeAllMenus();
  const proj = ((window.__DB__ && window.__DB__.projects) || []).find(x => x && x.id === id);
  if (!proj) return;
  _mvProjectId = id;
  const progs = _programmes().filter(P => !P.archived);
  const body = document.getElementById('mvBody');
  if (!body) return;
  /* ⚠ EXISTING PROGRAMMES ONLY. Creating one is its own flow on the portfolio; a picker that
     also creates things is two features in one control. */
  body.innerHTML = progs.length
    ? `<div class="mv-list">` + progs.map(function (P) {
        const isCur = String(P.id) === String(proj.programmeId);
        /* ⚠ The app's OWN counter, not a hand-rolled one. My first version counted every
           project including ARCHIVED ones, so the picker said "2 projects" beside a portfolio
           row saying "1 project" - two definitions of the same number on one screen, which is
           the exact class this repo keeps paying for. Found by looking at the rendered picker. */
        const n = _progProjectCount(P.id);
        return `<button class="mv-row" ${isCur ? 'disabled' : ''} ` +
          (isCur ? '' : `onclick="moveProjectTo('${_fxJsAttr(String(P.id))}')"`) + `>` +
          `<span class="mv-name">${_escapeHtml(P.name || '(untitled programme)')}</span>` +
          `<span class="mv-n">${n} project${n === 1 ? '' : 's'}</span>` +
          (isCur ? `<span class="mv-cur">current</span>` : ``) + `</button>`;
      }).join('') + `</div>`
    : `<div class="pf-empty">There is only one programme, so there is nowhere to move this project yet.</div>`;
  const t = document.getElementById('mvTitle');
  if (t) t.textContent = 'Move “' + (proj.name || 'this project') + '” to…';
  const ov = document.getElementById('mvOverlay');
  if (ov) ov.classList.add('open');
}
function closeMoveProject() {
  const ov = document.getElementById('mvOverlay');
  if (ov) ov.classList.remove('open');
  _mvProjectId = null;
}

/* The move itself. Returns the programme it CAME FROM, so "Move back" knows without the user
   having to remember. v17.1 also returns every predecessor relationship it removed. The low-
   level operation always enforces same-programme integrity; the UI wrapper confirms first. */
function _applyProjectMove(projectId, toProgId) {
  const proj = ((window.__DB__ && window.__DB__.projects) || []).find(x => x && x.id === projectId);
  if (!proj) return null;
  const from = proj.programmeId;
  if (String(from) === String(toProgId)) return null;
  const target = _programmes().find(P => String(P.id) === String(toProgId));
  if (!target) return null;
  const removedLinks = _predecessorLinksInvalidAfterMove(projectId, target.id);
  proj.programmeId = target.id;
  _removeProjectPredecessorLinks(removedLinks);
  /* v14.1 (b): the app derives the programme FROM the project, so a project we are standing in
     must re-point the rest of the app rather than leave it on the old programme. */
  if (typeof activeProjectId !== 'undefined' && activeProjectId === projectId
      && typeof activeProgrammeId !== 'undefined') activeProgrammeId = target.id;
  markDirty();
  refreshAll();
  if (document.body.classList.contains('view-portfolio')) renderPortfolio();
  return { from: from, to: target.id, toName: target.name || '', projName: proj.name || '',
           removedLinks: removedLinks };
}

function _requestProjectMove(projectId, toProgId, onDone) {
  const links = _predecessorLinksInvalidAfterMove(projectId, toProgId);
  const apply = function () {
    const res = _applyProjectMove(projectId, toProgId);
    if (res && typeof onDone === 'function') onDone(res);
  };
  if (!links.length) { apply(); return; }
  const n = links.length;
  showConfirm('Move project and remove relationships?',
    'Moving this project would put <strong>' + n + ' predecessor relationship'
      + (n === 1 ? '' : 's') + '</strong> across programme boundaries. '
      + 'Those relationships will be removed; unavailable stored references will be kept.',
    apply);
}

function moveProjectTo(toProgId) {
  const id = _mvProjectId;
  closeMoveProject();
  _requestProjectMove(id, toProgId, function (res) {
    _moveToast(res.projName, res.toName, id, res.from, res.removedLinks.length);
  });
}

/* ⚠ "MOVE BACK" IS A SECOND MOVE, NEVER `pendingUndo` — the scope's one invariant for this
   item, and it is not a style choice. showUndoToast() calls clearPendingUndo() on entry, which
   COMMITS whatever delete is pending, and its button fires pendingUndo.restore rather than the
   callback it was handed. Routing a move through it would silently commit a delete the user
   could still have undone — killing a live "action deleted — Undo" just by moving a project.
   So this borrows the toast's CSS and owns its own lifecycle, exactly as v14.2 (d)'s
   _hiddenJumpToast does. A no-loss move still happens immediately. When a move in either
   direction would invalidate predecessor relationships, _requestProjectMove uses the house
   confirmation first and names that those relationships will be removed. */
function _moveToast(projName, toName, projectId, fromProgId, removedCount) {
  let host = document.getElementById('toastHostBottomLeft');
  if (!host) {
    host = document.createElement('div');
    host.id = 'toastHostBottomLeft';
    document.body.appendChild(host);
  }
  const el = document.createElement('div');
  el.className = 'toast toast-undo';
  const span = document.createElement('span');
  span.textContent = '“' + (projName || 'Project') + '” moved to ' + (toName || 'that programme')
    + (removedCount ? ' · ' + removedCount + ' predecessor relationship'
      + (removedCount === 1 ? '' : 's') + ' removed' : '');
  el.appendChild(span);
  const btn = document.createElement('button');
  btn.className = 'toast-undo-btn';
  btn.textContent = 'Move back';
  const close = function () {
    el.classList.remove('toast-visible');
    setTimeout(function () { if (el.parentNode) el.parentNode.removeChild(el); }, 200);
  };
  btn.onclick = function () {
    close();
    _requestProjectMove(projectId, fromProgId, function (back) {
      showToast('“' + back.projName + '” moved back to ' + back.toName
        + (back.removedLinks.length ? ' · predecessor relationships removed' : ''));
    });
  };
  el.appendChild(btn);
  host.appendChild(el);
  void el.offsetWidth;
  el.classList.add('toast-visible');
  setTimeout(close, 6000);
}

function _projMenuItems(id) {
  /* v10.4 (W3): archive-aware. The landing page's separate archived list — and the
     Unarchive button that lived on it — went with the cards; archived projects are
     dimmed rows in the one table now, so Unarchive has to live here. One menu doing
     both directions, rather than a second affordance parked somewhere else. */
  const _c = ((window.__DB__ && window.__DB__.projects) || []).find(x => x.id === id);
  const _arch = !!(_c && _c.archived);
  return `<button class="proj-menu-item" onclick="projMenuRename('${id}', event)">Rename</button>`
    + (_arch
        ? `<button class="proj-menu-item" onclick="projMenuUnarchive('${id}', event)">Unarchive</button>`
        : `<button class="proj-menu-item" onclick="projMenuArchive('${id}', event)">Archive</button>`)
    + `<button class="proj-menu-item" onclick="openPredecessorEditor('${id}', event)">Set predecessors…</button>`
    + `<button class="proj-menu-item" onclick="openMoveProject('${id}', event)">Move to programme…</button>`
    + `<button class="proj-menu-item" onclick="projMenuExport('${id}', event)">Export…</button>`
    + `<div class="proj-menu-sep"></div>`
    + `<button class="proj-menu-item danger" onclick="projMenuDelete('${id}', event)">Delete…</button>`;
}
/* v12.1 (W1): generalised for programmes rather than copied — build standard 2. Both
   buttons carry .pm-menu-btn, so _placeRowMenu and the outside-click handler work
   unchanged for either level. */
function toggleCardMenu(e, id, kind) {
  if (e) e.stopPropagation();
  const isProg = (kind === 'programme');
  const isPortfolioProject = (kind === 'portfolio-project');
  const menu = document.getElementById((isProg ? 'progMenu-' : isPortfolioProject ? 'pfProjectMenu-' : 'cardMenu-') + id);
  if (!menu) return;
  const wasHidden = menu.hidden;
  _closeAllMenus();
  if (!wasHidden) return;          // it was open → leave closed (toggle off)
  menu.innerHTML = isProg ? _pfMenuItems(id) : _projMenuItems(id);
  menu.hidden = false;
  /* v10.4.1: place it now that it has content and can be measured. */
  const btn = (e && (e.currentTarget || e.target) && (e.currentTarget || e.target).closest)
    ? (e.currentTarget || e.target).closest('.pm-menu-btn')
    : null;
  _placeRowMenu(menu, btn || document.querySelector('.pm-menu-btn[data-id="' + CSS.escape(String(id)) + '"]'));
}
/* ── v10.4.1: position the row's ⋯ menu ──────────────────────────────────────────
   The menu is position:fixed (see the CSS note) so it is not clipped by the table
   wrapper's scroll context, which means it needs real viewport coordinates. Right-
   aligned to the button, below it by default, FLIPPED ABOVE when there is no room —
   a row low in a long list hits the same edge the single-row table hit. Clamped to
   the viewport on both axes so it can never be placed off-screen. */
function _placeRowMenu(menu, btn) {
  if (!menu || !btn) return;
  const PAD = 8, GAP = 6;
  const r = btn.getBoundingClientRect();
  const m = menu.getBoundingClientRect();
  let left = r.right - m.width;                       // right edges aligned
  left = Math.max(PAD, Math.min(left, window.innerWidth - m.width - PAD));
  let top = r.bottom + GAP;
  if (top + m.height > window.innerHeight - PAD) top = r.top - GAP - m.height;   // flip up
  top = Math.max(PAD, Math.min(top, window.innerHeight - m.height - PAD));
  menu.style.left = left + 'px';
  menu.style.top = top + 'px';
}
/* A fixed menu does not travel with the page, so any scroll would strand it next to
   nothing. Close instead of chasing — capture phase, so the table's OWN horizontal
   scroller counts as well as the window. Same for a resize. */
/* ── v13.1 (A60): a scroll listener must not make the browser wait ─────────────────────
   This ran a document-wide querySelectorAll on EVERY scroll event, in the capture phase and
   NON-PASSIVE — so the browser had to run it and wait for it before it could paint the
   scroll, on a portfolio of any size, only to discover almost every time that no menu was
   open. Two changes: the listeners are PASSIVE (the browser stops blocking on them, which
   is the dominant win and costs nothing — neither handler ever called preventDefault), and
   the common case exits on a single short-circuiting query instead of materialising a
   NodeList of every menu and walking it. ⚠ The query is REDUCED, not eliminated: removing
   it entirely needs an open-menu counter maintained by every path that unhides one, and a
   missed path would leave menus that never close — a worse defect than the cost being
   fixed. Recorded as the deliberate stopping point. */
function _closeRowMenus() {
  if (!document.querySelector('.ov-card-menu:not([hidden])')) return;
  document.querySelectorAll('.ov-card-menu').forEach(m => { if (!m.hidden) m.hidden = true; });
}
window.addEventListener('scroll', _closeRowMenus, { capture: true, passive: true });
window.addEventListener('resize', _closeRowMenus, { passive: true });
function toggleProjMenu(e) {
  if (e) e.stopPropagation();
  const menu = document.getElementById('projMenu');
  if (!menu) return;
  const wasHidden = menu.hidden;
  _closeAllMenus();
  if (!wasHidden) return;
  if (typeof activeProjectId === 'undefined' || activeProjectId == null) return;
  menu.innerHTML = _projMenuItems(activeProjectId);
  menu.hidden = false;
}
/* Rename — inline, in place. Context comes from the current view: a card on the
   overview, otherwise the breadcrumb name in the project view. */
function projMenuRename(id, e) {
  if (e) e.stopPropagation();
  _closeAllMenus();
  if (document.body.classList.contains('view-portfolio')) {
    const rowEl = document.querySelector(`#portfolioView .pf-project-hit[data-project-id="${CSS.escape(String(id))}"]`);
    const nameEl = rowEl && rowEl.querySelector('.pf-project-hit-name');
    if (nameEl) _inlineRename(nameEl, id, () => renderPortfolio());
  } else if (document.body.classList.contains('view-overview')) {
    /* v10.4 (W3): the rename target moved from the card's name to the table row's.
       _inlineRename already stops click/mousedown propagation, so editing in the row
       never triggers the row's own open-the-project handler. */
    const rowEl = document.querySelector(`#overviewView .pm-row[data-id="${id}"]`);
    const nameEl = rowEl && rowEl.querySelector('.pm-name');
    if (nameEl) _inlineRename(nameEl, id, () => renderOverview());
  } else {
    const nameEl = document.getElementById('crumbProjName');
    if (nameEl) _inlineRename(nameEl, id, () => {
      const c = ((window.__DB__ && window.__DB__.projects) || []).find(x => x.id === id);
      const input = document.querySelector('.crumb-proj .ov-rename-input');
      if (input) { const s = document.createElement('span'); s.id = 'crumbProjName'; s.textContent = (c && c.name) || 'Project'; input.replaceWith(s); }
    });
  }
}
function projMenuArchive(id, e) {
  if (e) e.stopPropagation();
  _closeAllMenus();
  if (!setProjectArchived(id, true)) return;
  if (document.body.classList.contains('view-overview')) renderOverview();
  else gotoOverview();             // an archived project can't stay open
}
/* v10.4 (W3): the other direction. Replaces the Unarchive button that used to sit on
   the landing page's separate archived list, which the single table retired. */
function projMenuUnarchive(id, e) {
  if (e) e.stopPropagation();
  _closeAllMenus();
  if (setProjectArchived(id, false)) {
    if (document.body.classList.contains('view-portfolio')) renderPortfolio();
    else renderOverview();
  }
}
/* shared inline-rename editor: swap the name element for an input; commit on
   Enter/blur, cancel on Escape. Stops propagation so it never triggers the card's
   navigation or the switcher. */
function _inlineRename(nameEl, id, onDone, kind) {
  /* v12.1 (W1): generalised to programmes — one editor, two record types. */
  const isProg = (kind === 'programme');
  const cur = isProg
    ? ((_programmes().find(p => p && p.id === id) || {}).name || '')
    : ((((window.__DB__ && window.__DB__.projects) || []).find(c => c.id === id) || {}).name || '');
  const input = document.createElement('input');
  input.className = 'ov-rename-input';
  input.type = 'text';
  input.value = cur;
  input.maxLength = 80;
  let done = false;
  const finish = (cancel, blurEvent) => {
    if (done) return; done = true;
    const v = (input.value || '').trim();
    if (!cancel && v && v !== cur) { if (isProg) renameProgramme(id, v); else renameProject(id, v); }
    /* v17.5 P4: a native Order select needs to survive the pointer gesture that blurs this
       editor. Commit the text now, then let the select's change repaint in the new order; if
       the user opens and dismisses it unchanged, its later blur performs the ordinary repaint. */
    const destination = blurEvent && blurEvent.relatedTarget;
    if (!cancel && destination && destination.classList
        && destination.classList.contains('list-order-select')) {
      destination.addEventListener('blur', function () {
        if (typeof onDone === 'function') onDone();
      }, { once: true });
      return;
    }
    if (!cancel && _listOrderIsInteractionTarget(destination)) {
      _listOrderDeferEditorRepaint(destination, onDone);
      return;
    }
    if (typeof onDone === 'function') onDone();
  };
  input.addEventListener('click', e => e.stopPropagation());
  input.addEventListener('mousedown', e => e.stopPropagation());
  input.addEventListener('keydown', e => {
    e.stopPropagation();
    if (e.key === 'Enter') { e.preventDefault(); finish(false); }
    else if (e.key === 'Escape') { e.preventDefault(); finish(true); }
  });
  _onBlurCommit(input, e => finish(false, e));
  nameEl.replaceWith(input);
  input.focus(); input.select();
}
/* card is a div[role=button]; activate unless a rename input is live inside it. */
/* ── Delete — type-the-name confirm → full erasure (no undo). ── */
let _deleteProjectId = null;
/* v12.1 (W1): the SAME type-the-name modal serves programmes — the stronger confirm the
   house already uses for an irreversible-at-Save delete, and a cascade deserves at least
   as much. Generalised rather than duplicated (build standard 2). */
let _deleteKind = 'project';
function openDeleteProgramme(id) {
  _closeAllMenus();
  const P = _programmes().find(p => p && p.id === id);
  if (!P) return;
  _deleteProjectId = id; _deleteKind = 'programme';
  const kids = (((window.__DB__ && window.__DB__.projects) || [])
    .filter(c => c && c.programmeId === id)).length;
  const t = document.getElementById('deleteProjectTitle');
  if (t) t.textContent = 'Delete programme?';
  const lab = document.getElementById('deleteProjectLabel');
  if (lab) lab.textContent = 'Type the programme name to confirm';
  const btn = document.getElementById('deleteProjectBtn');
  if (btn) { btn.textContent = 'Delete programme'; btn.disabled = true; }
  const msg = document.getElementById('deleteProjectMsg');
  if (msg) msg.innerHTML = `This deletes <strong>${_escapeHtml(P.name || 'this programme')}</strong>`
    + (kids ? ` <strong>and its ${kids} project${kids === 1 ? '' : 's'}</strong>, with all of their data` : ` (it has no projects)`)
    + `, plus its benefits register. You get a few seconds to undo; after that, the next Save writes the file without it and there is no recovery. To archive instead (fully reversible), cancel and use Archive.`;
  const input = document.getElementById('deleteProjectInput');
  if (input) input.value = '';
  const ov = document.getElementById('deleteProjectOverlay');
  if (ov) ov.classList.add('open');
  if (input) input.focus();
}
/* the name the typed value must match, whichever kind is in play */
function _deleteTargetName() {
  if (_deleteKind === 'programme') {
    const P = _programmes().find(p => p && p.id === _deleteProjectId);
    return P ? (P.name || '') : null;
  }
  const c = ((window.__DB__ && window.__DB__.projects) || []).find(x => x.id === _deleteProjectId);
  return c ? (c.name || '') : null;
}
function projMenuDelete(id, e) {
  if (e) e.stopPropagation();
  openDeleteProject(id);          // closes the menu + opens the confirm modal
}
function openDeleteProject(id) {
  _deleteKind = 'project';   /* v12.1: the modal is shared — always state which kind */
  const t = document.getElementById('deleteProjectTitle');
  if (t) t.textContent = 'Delete project?';
  const lab = document.getElementById('deleteProjectLabel');
  if (lab) lab.textContent = 'Type the project name to confirm';
  const bt = document.getElementById('deleteProjectBtn');
  if (bt) bt.textContent = 'Delete project';
  _closeAllMenus();
  const c = ((window.__DB__ && window.__DB__.projects) || []).find(x => x.id === id);
  if (!c) return;
  _deleteProjectId = id;
  const msg = document.getElementById('deleteProjectMsg');
  /* v10.3 (W4): the copy said "can't be undone, and there is no recovery" and then handed
     the user a 6-second Undo toast — so anyone who deleted by mistake wouldn't even look
     for it. State what actually happens: a short undo window, then it is gone at Save. */
  if (msg) msg.innerHTML = `This deletes <strong>${_escapeHtml(c.name || '')}</strong> and all of its data — actions, sessions, stakeholders, risks and notes. You get a few seconds to undo; after that, the next Save writes the file without it and there is no recovery. To archive instead (fully reversible), cancel and use Archive.`;
  const input = document.getElementById('deleteProjectInput');
  if (input) input.value = '';
  const btn = document.getElementById('deleteProjectBtn');
  if (btn) btn.disabled = true;
  const ov = document.getElementById('deleteProjectOverlay');
  if (ov) ov.classList.add('open');
  if (input) input.focus();
}
/* Delete stays disabled until the typed name matches the project name exactly. */
function onDeleteProjectInput() {
  const nm = _deleteTargetName();
  const input = document.getElementById('deleteProjectInput');
  const btn = document.getElementById('deleteProjectBtn');
  if (nm === null || !input || !btn) return;
  btn.disabled = (input.value.trim() !== nm.trim());
}
function closeDeleteProject() {
  const ov = document.getElementById('deleteProjectOverlay');
  if (ov) ov.classList.remove('open');
  _deleteProjectId = null;
}
function handleDeleteProjectOverlayClick(e) { if (e.target === e.currentTarget) closeDeleteProject(); }
function confirmDeleteProject() {
  const input = document.getElementById('deleteProjectInput');
  const nm = _deleteTargetName();
  if (nm === null || !input) return;
  if (input.value.trim() !== nm.trim()) return;   /* final guard — exact match only */
  if (_deleteKind === 'programme') {
    /* ◆A: one act, ONE undo, through the SHARED pendingUndo slot — the 11th delete type */
    const id = _deleteProjectId;
    const wasActive = (activeProgrammeId === id);
    const restore = deleteProgrammeCascade(id);
    if (!restore) return;
    closeDeleteProject();
    if (wasActive) { activeProgrammeId = ''; activeProjectId = null; }
    /* the hash may already be #/portfolio, and setting a hash to its current value fires
       no hashchange — repaint explicitly (the recorded gotoOverview gotcha). */
    gotoPortfolio(); showPortfolio();
    showUndoToast('Programme deleted', () => {
      restore();
      if (wasActive) activeProgrammeId = id;
      showPortfolio();
      if (typeof showToast === 'function') showToast('Programme restored', { type: 'info' });
    }, 'portfolio');   /* v15: restores by array index into db — no stale per-project binding */
    return;
  }
  const id = _deleteProjectId;
  const db = window.__DB__;
  const projects = (db && db.projects) || [];
  const removedIndex = projects.findIndex(x => x && x.id === id);
  const c = projects[removedIndex];
  /* v12.1: `input` and the typed-name guard now live in the shared preamble above — this
     branch only needs the record itself. */
  if (!c) return;
  const removed = c;                                          // capture for undo
  const onOverview = document.body.classList.contains('view-overview');
  const onPortfolio = document.body.classList.contains('view-portfolio');
  /* ⚠⚠ v16.8 (W3): CAPTURE THE PRUNED BENEFIT LINKS FOR THE UNDO. A prune the undo does not
     reverse would trade a silent wrong number for a silently lost link — the same class of
     defect one step along, and it would satisfy the prune check perfectly.
     This is `deleteStakeholder`'s v10.3 (W4) idiom exactly: sweep, capture, put back. */
  const sweptBenefitLinks = [];
  const sweptPredecessorLinks = [];
  deleteProject(id, sweptBenefitLinks, sweptPredecessorLinks);
  closeDeleteProject();
  if (onPortfolio) renderPortfolio();
  else if (onOverview) renderOverview();
  else gotoOverview();            // we were viewing the (now-deleted) project
  showUndoToast('Project deleted', () => {
    const arr = (window.__DB__ && window.__DB__.projects) || [];
    arr.splice(Math.max(0, Math.min(removedIndex, arr.length)), 0, removed);
    _restoreProjectPredecessorLinks(sweptPredecessorLinks);
    /* v16.8 (W3): and the benefit links this delete swept, back where they were */
    sweptBenefitLinks.forEach(({ progId, benId }) => {
      const P = ((window.__DB__ && window.__DB__.programmes) || []).find(x => x && x.id === progId);
      const b = P && Array.isArray(P.benefits) ? P.benefits.find(y => y && y.id === benId) : null;
      if (!b) return;
      if (!Array.isArray(b.projectIds)) b.projectIds = [];
      if (b.projectIds.indexOf(id) === -1) b.projectIds.push(id);
    });
    if (typeof markDirty === 'function') markDirty();
    if (onPortfolio && typeof renderPortfolio === 'function') renderPortfolio();
    else if (typeof renderOverview === 'function') renderOverview();
    if (typeof showToast === 'function') showToast('Project restored', { type: 'info' });
    /* v15: scope 'portfolio' — this splices into db.projects, so no per-project binding */
  }, 'portfolio');
}

/* ── per-project export — a data file containing only the chosen project(s).
   Reuses serializeDB on a subset; always downloads under the _export name (never
   in-place over the live data file). ── */
function _downloadText(text, filename) {
  try {
    const blob = new Blob([text], { type: 'text/javascript' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
    return true;
  } catch (err) { console.error('export download failed:', err); return false; }
}
/* v5.1 §6: local timestamp YYYYMMDD_HHMMSS for export filenames (to the second). */
function _tsStamp() {
  const d = new Date(), p = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}${p(d.getMonth()+1)}${p(d.getDate())}_${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`;
}
function projMenuExport(id, e) {
  if (e) e.stopPropagation();
  openExport(id);                 // openExport closes the menu + pre-selects this project
}
function openExport(preselectId) {
  if (_progressBlockSaveOrExport()) return;
  _closeAllMenus();
  const all = ((window.__DB__ && window.__DB__.projects) || []);
  const host = document.getElementById('exportList');
  /* v12.3 (W4): GROUPED by programme, in programmes[] order. The list was always
     portfolio-wide; grouping is what makes that honest now that it opens from the portfolio.
     Selection, the archived label, the overall select-all and the bytes written are unchanged -
     confirmExport still reads only the checked ids, so grouping cannot alter the output. */
  const _row = (c, gi) => {
    const checked = (preselectId && c.id === preselectId) ? ' checked' : '';
    const arch = c.archived ? ` <span class="export-arch">(archived)</span>` : '';
    return `<label class="export-row"><input type="checkbox" class="export-cb" data-g="${gi}" value="${_escapeHtml(c.id)}" onchange="onExportChange()"${checked}> <span>${_escapeHtml(c.name || 'Untitled project')}</span>${arch}</label>`;
  };
  if (host) {
    if (!all.length) { host.innerHTML = `<div class="ov-empty">No projects to export.</div>`; }
    else {
      const seen = new Set();
      let html = '';
      _programmes().forEach((P, gi) => {
        const mine = all.filter(c => c && c.programmeId === P.id);
        if (!mine.length) return;   /* an empty programme needs no heading */
        mine.forEach(c => seen.add(c.id));
        html += `<div class="export-group">`
          + `<label class="export-row export-ghead"><input type="checkbox" class="export-gcb" data-g="${gi}" onchange="toggleExportGroup(this)"> `
          + `<span>${_escapeHtml(P.name || 'Unnamed programme')}</span>`
          + `<span class="export-gcount">${mine.length} project${mine.length === 1 ? '' : 's'}</span></label>`
          + mine.map(c => _row(c, gi)).join('')
          + `</div>`;
      });
      /* A project whose programmeId names nothing. _normProgrammes adopts orphans at load, so
         this can only arise mid-session - but an UNEXPORTABLE project would be far worse than an
         odd heading, so they are listed rather than silently dropped. */
      const orphans = all.filter(c => c && !seen.has(c.id));
      if (orphans.length) {
        html += `<div class="export-group">`
          + `<label class="export-row export-ghead"><input type="checkbox" class="export-gcb" data-g="x" onchange="toggleExportGroup(this)"> `
          + `<span>Not in any programme</span>`
          + `<span class="export-gcount">${orphans.length} project${orphans.length === 1 ? '' : 's'}</span></label>`
          + orphans.map(c => _row(c, 'x')).join('')
          + `</div>`;
      }
      host.innerHTML = html;
    }
  }
  onExportChange();
  const ov = document.getElementById('exportOverlay');
  if (ov) ov.classList.add('open');
}
function onExportChange() {
  const all = document.querySelectorAll('#exportList .export-cb');
  const n = document.querySelectorAll('#exportList .export-cb:checked').length;
  const btn = document.getElementById('exportBtn');
  if (btn) { btn.disabled = (n === 0); btn.textContent = (n > 1) ? `Export ${n} projects` : 'Export'; }
  const sa = document.getElementById('exportSelectAll');
  if (sa) { sa.checked = (all.length > 0 && n === all.length); sa.indeterminate = (n > 0 && n < all.length); }
  /* v12.3 (W4): each programme heading mirrors its own members - checked, empty or
     indeterminate. Derived here rather than tracked, so it cannot fall out of step with the
     boxes, and the overall select-all keeps working untouched because it ends in this function. */
  document.querySelectorAll('#exportList .export-gcb').forEach(g => {
    const kids = [...document.querySelectorAll('#exportList .export-cb[data-g="' + g.dataset.g + '"]')];
    const k = kids.filter(cb => cb.checked).length;
    g.checked = (kids.length > 0 && k === kids.length);
    g.indeterminate = (k > 0 && k < kids.length);
  });
}
/* v12.3 (W4): one programme heading toggles its own members and nothing else. */
function toggleExportGroup(g) {
  document.querySelectorAll('#exportList .export-cb[data-g="' + g.dataset.g + '"]')
    .forEach(cb => { cb.checked = g.checked; });
  onExportChange();
}
function closeExport() { const ov = document.getElementById('exportOverlay'); if (ov) ov.classList.remove('open'); }
function handleExportOverlayClick(e) { if (e.target === e.currentTarget) closeExport(); }
function confirmExport() {
  if (_progressBlockSaveOrExport()) return;
  const ids = [...document.querySelectorAll('#exportList .export-cb:checked')].map(cb => cb.value);
  if (!ids.length) return;
  /* v10.4 (W1): the SAME pipeline as Save, minus the two save-only steps.
       CAPTURE - yes: a backup must reflect what is on screen, including text the
                 user has typed but not yet blurred. This used to be a partial,
                 hand-maintained copy of Save's capture (three of its steps) and is
                 exactly how the two paths drifted apart in the first place.
       STAMP   - NO. The export carries the last REAL save's savedAt and each
                 project's existing appMeta.lastSaved, because no save happened.
                 Stamping here would put a save time in a file no save produced.
       COMMIT  - NO, and this is the important one: committing would clear isDirty
                 and the pending undo, so taking a backup would make the app believe
                 you had saved while the real data file sat untouched. That is a
                 worse bug than the one this refactor exists to prevent.
     Because step 3 computes the action bake instead of applying it, a select-all
     export is byte-identical to what a save would write, bar the stamp keys -
     enforced by the export_equals_save check. */
  _captureAllFromDOM();
  /* v14 (W4a): the SAME plan a save would write, so a select-all export stays byte-identical
     to a save (export_equals_save). _revPlan is pure and _buildSaveDB stages a copy, so
     nothing here publishes anything — export still never calls _revApply. */
  const picked = _buildSaveDB(ids, _buildActionObjects(),
                              _revPlan(window.__DB__, _revBaseline, _myWriterId()));
  if (!picked.projects.length) return;
  /* ⚠ v14.0.1 (W2): `TrendAI_FlightDeck_export_` - and `data` is dropped DELIBERATELY, his
     call at the GO. The stem matches neither PART_RE nor list_parts's glob, so an export can
     never be mistaken for somebody's part by any pattern, in this app or the launcher. That is
     the same self-enforcing property the v14 rename bought, applied to the one file type a user
     is invited to carry around. The trade he took knowingly: recognisability, since the old
     name shared a stem with the data file. Structure won. */
  const ok = _downloadText(serializeDB(picked), `TrendAI_FlightDeck_export_${_tsStamp()}.js`);
  closeExport();
  /* v13.1 (A38): `picked` is the DB OBJECT, so `picked.length` was always undefined and
     `undefined > 1` always false — the toast said "Exported 1 project" no matter how many
     went. The count lives on the array. */
  const _n = picked.projects.length;
  if (ok && typeof showToast === 'function') showToast(_n > 1 ? `Exported ${_n} projects` : 'Exported 1 project');
}
/* v5.1 §5: select-all toggle in the export dialog → full-DB backup. */
function toggleExportSelectAll(on) {
  document.querySelectorAll('#exportList .export-cb').forEach(cb => { cb.checked = on; });
  onExportChange();
}

/* ───────── v5.2.2: import projects from an exported data file ─────────
   Reads a TrendAI data file the user picks, lists its accounts (flagging id
   collisions with the current DB), and merges them in. Conflicts resolve by the
   user's choice: import as copies (default, no data loss), replace, or skip. */
let _importStaged = null;

function openImport() {
  if (typeof _closeAllMenus === 'function') _closeAllMenus();
  _importStaged = null;
  const fi = document.getElementById('importFile'); if (fi) fi.value = '';
  const prev = document.getElementById('importPreview'); if (prev) prev.innerHTML = '';
  const btn = document.getElementById('importBtn'); if (btn) btn.disabled = true;
  const ov = document.getElementById('importOverlay'); if (ov) ov.classList.add('open');
}
function closeImport() { const ov = document.getElementById('importOverlay'); if (ov) ov.classList.remove('open'); _importStaged = null; }
function handleImportOverlayClick(e) { if (e.target === e.currentTarget) closeImport(); }

/* Parse a data file's text into its projects[]. v18: PARSED, NEVER EXECUTED. This used to run
   the file with `new Function('window', text)` — global scope, so `document`, `fetch` and the
   session cookie's same-origin API were all reachable from a file somebody handed you. It now
   goes through _parsePartText, the parser boot has used for parts since v14 W4a, which accepts
   only what serializeDB can produce. */
function _parseImportText(text) {
  let db = null;
  try { db = _parsePartText(String(text)); }
  catch (e) { db = null; }
  /* v13.1 (A55): "accounts" is retired vocabulary — they have been PROJECTS since v7, and
     this string was the last place a user could still meet the old word. */
  if (!db || !Array.isArray(db.projects)) throw new Error('That file isn’t a TrendAI data file (no projects found in it).');
  /* ── ⚠ v13.1 (A13): THE SCHEMA GATE. This was the only load path without one ───────────
     Boot checks the schema and empties projects on a mismatch; _adoptDB refuses a wrong
     schema outright. Import merged a file of ANY vintage straight into the live portfolio,
     field by field, with no version check at all — the one route by which a file the app
     cannot open could still get its contents in. There is deliberately NO migration path in
     this project (see the boot gate), so the only honest answer is a refusal that names
     both numbers. Refused BEFORE any project or programme is read, so nothing is half-taken. */
  if (db.schemaVersion !== SCHEMA_VERSION) {
    throw new Error('That file was written for schema ' + JSON.stringify(db.schemaVersion)
      + ', but this app reads schema ' + SCHEMA_VERSION
      + '. Importing it could corrupt your portfolio, so nothing was taken from it.');
  }
  /* ⚠⚠ v15.2: TOMBSTONES ARE HARVESTED HERE, exactly as boot harvests them — import was the
     only ingestion path that did not, and every consequence followed: a deleted project came
     back as a renderable husk that self-deleted (with anything typed into it) at the next
     load; "Replace my existing version" could replace a LIVE project with a deletion and then
     publish that deletion team-wide; a tombstoned programme resurrected as a ghost "Unnamed
     programme"; and child tombstones sat as blank ghost rows in registers whose renderers
     deliberately carry no !r.del filter (the engine's own invariant says del records never
     sit in live arrays). Exports legitimately CARRY tombstones — that is the delete-beats-
     stale-copy mechanism — so the reader must strip them, not the writer. */
  const _stripDel = arr => arr.filter(r => !(r && typeof r === 'object' && r.del));
  const projects = _stripDel(db.projects.filter(c => c && typeof c === 'object'));
  projects.forEach(c => {
    ['actions', 'sessions', 'stakeholders', 'risks', 'assumptions', 'workstreams'].forEach(k => {
      if (Array.isArray(c[k])) c[k] = _stripDel(c[k]);
    });
    if (c.appMeta && typeof c.appMeta === 'object') {
      ['changeRequests', 'communications', 'improvements'].forEach(k => {
        if (Array.isArray(c.appMeta[k])) c.appMeta[k] = _stripDel(c.appMeta[k]);
      });
    }
  });
  if (!projects.length) throw new Error('No projects found in that file.');
  /* ── v10.4.3: the PROGRAMME branch comes across too ──────────────────────────────
     v10.3's W2 fixed the export side: a select-all export is offered as a
     "full-portfolio backup" and until then silently omitted `programme` entirely,
     discarding programme RAG, its rationale and the whole benefits register. Nobody
     checked the reader. This function returned `db.projects` only, so Import threw the
     same branch away without a word — the round trip was asymmetric, and a backup taken
     via Export was not fully restorable via Import.
     Sanitised here rather than trusting the file: this text came off disk and may be
     hand-edited. Returns null when the file genuinely has no programme branch (a
     pre-v10.0 export), which the caller treats as "nothing to offer". */
  /* v11: the branch is PLURAL. A fork of the portfolio file carries every programme, so
     Import has to carry every programme — the same asymmetry v10.4.3 fixed, one shape
     later. Still sanitised rather than trusted: this text came off disk. Returns [] when
     the file has no programmes branch at all (a pre-v11 export), which the caller treats
     as "nothing to offer" rather than as an error. */
  /* ── ⚠ v13.1 (A15): carry EVERY key, not the four this list happened to name ───────────
     `manager`, `archived` and `benSeq` were all being discarded here — so restoring a
     backup un-archived every archived programme, forgot who managed it, and reset the
     benefit high-water mark so a later add reused a BEN-### number that had been issued.
     The root cause is a structural mismatch: the serialiser writes `programmes` WHOLESALE
     precisely so a new key rides along for free, while the READER was a hand-picked list.
     One side gained keys, the other did not, and nothing failed loudly.
     So: start from a JSON-safe copy and then coerce the four whose SHAPE matters. The JSON
     round trip IS the sanitisation this function has always insisted on — functions,
     prototypes and undefined cannot survive it, and a cycle throws into the catch — while
     an unknown key is preserved rather than silently dropped. */
  const rawProgs = Array.isArray(db.programmes) ? db.programmes : [];
  /* v15.2: programme tombstones stripped for the same reason as project ones above */
  const programmes = rawProgs.filter(P => P && typeof P === 'object' && !P.del).map(P => {
    let carried;
    try { carried = JSON.parse(JSON.stringify(P)); } catch (e) { carried = null; }
    if (!carried || typeof carried !== 'object' || Array.isArray(carried)) carried = {};
    delete carried.id; delete carried.name; delete carried.rag; delete carried.benefits;
    return Object.assign(carried, {
      id: (typeof P.id === 'string' && P.id) ? P.id : '',
      name: (typeof P.name === 'string') ? P.name : '',
      rag: {
        level: (P.rag && ['red', 'amber', 'green'].includes(P.rag.level)) ? P.rag.level : null,
        rationale: (P.rag && typeof P.rag.rationale === 'string') ? P.rag.rationale : ''
      },
      benefits: Array.isArray(P.benefits) ? P.benefits.filter(b => b && typeof b === 'object' && !b.del) : []
    });
  });
  return { projects: projects, programmes: programmes };
}

/* ── v11 (◆E): does this project DIFFER from mine? ────────────────────────────────────
   A binary answer, deliberately. After a fork most colliding projects are the same
   untouched project in both files, and this isolates the two or three worth looking at.
   It says nothing about WHAT differs — that is the record-level picker, which stays
   parked (C2) precisely because it needs tombstones to be safe.

   Key order is normalised so two serialisers cannot disagree cosmetically, and a short
   list of VOLATILE keys is excluded: they change as you merely look at a project, and
   would otherwise make everything read "differs" and the badge worthless.
   ⚠ The exclusion list is a judgement, so this errs toward "differs" — the safe
   direction, because the badge only ever tells you to LOOK, never that you needn't. */
const _IMPORT_VOLATILE = ['uiState', 'lastSaved', 'sessionSnapshot', 'meetingTimer', 'focusActionId'];
function _stableJson(v) {
  if (v === null || typeof v !== 'object') return JSON.stringify(v) || 'null';
  if (Array.isArray(v)) return '[' + v.map(_stableJson).join(',') + ']';
  return '{' + Object.keys(v).sort().map(k => JSON.stringify(k) + ':' + _stableJson(v[k])).join(',') + '}';
}
/* ── ⚠ v13.1 (A65): compare only what the SERIALISER emits ──────────────────────────────
   This used to walk every own key of the live project object, and `setActiveProject`
   attaches a runtime one the moment you open a project: `_deletedIds`, a Set the serialiser
   never writes. So every project merely VISITED this session carried a key its own exported
   copy could not have, the content keys differed, and the badge fired on all of them — a
   badge that flags everything says nothing, which is the whole point of it being binary.
   Naming the serialised fields instead of subtracting the runtime ones kills the class
   rather than one instance: a key that is not written to the file cannot make two files
   differ, so nothing added to a project at runtime can ever reach this comparison again.
   ⚠ Keep in step with serializeDB's ordered list. `id` and `programmeId` are deliberately
   absent: they are identity and plumbing, and both are remapped on import by design. */
const _PROJECT_CONTENT_KEYS = ['uid', 'name', 'archived', 'actions', 'sessions',
  'stakeholders', 'risks', 'assumptions', 'workstreams', 'edits', 'newActions', 'appMeta'];
function _projectContentKey(c) {
  if (!c || typeof c !== 'object') return '';
  const copy = {};
  _PROJECT_CONTENT_KEYS.forEach(k => {
    if (c[k] !== undefined) copy[k] = c[k];
  });
  if (copy.appMeta && typeof copy.appMeta === 'object') {
    const am = {};
    Object.keys(copy.appMeta).forEach(k => { if (_IMPORT_VOLATILE.indexOf(k) === -1) am[k] = copy.appMeta[k]; });
    copy.appMeta = am;
  }
  return _stableJson(_importScrubBookkeeping(copy));   /* v15.2 — see the scrubber above */
}
/* ⚠ v15.2: merge bookkeeping is scrubbed from the badge compare. Exports fold child
   tombstones into the arrays and carry per-record rev stamps; the live side is harvested and
   differently-stamped — so any project with delete history read "differs" against its own
   unchanged export: A65's "a badge that flags everything says nothing", one level down. */
function _importScrubBookkeeping(v) {
  if (Array.isArray(v)) {
    return v.filter(x => !(x && typeof x === 'object' && x.del)).map(_importScrubBookkeeping);
  }
  if (v && typeof v === 'object') {
    const o = {};
    Object.keys(v).forEach(k => {
      if (k === 'rev' || k === 'writer' || k === 'del') return;
      o[k] = _importScrubBookkeeping(v[k]);
    });
    return o;
  }
  return v;
}
/* ⚠ v15.2: on REPLACE, the surviving record carries FORWARD the higher rev — the file being
   imported may be an older backup whose records sit at rev 3 while colleagues hold my rev 7;
   installed verbatim, my next save would stamp rev 4 and the next merge would silently undo
   the user's explicit replace (the supersede note fires only on ties). With the higher rev
   carried, the post-import save stamps rev 8 and the replace propagates as intended. */
function _importCarryRevs(mine, incoming) {
  const lift = (a, b) => {
    if (a && b && typeof a === 'object' && typeof b === 'object'
        && (a.rev | 0) > (b.rev | 0)) b.rev = a.rev;
  };
  const resolve = (host, path) => {
    const segs = String(path).split('.');
    for (let i = 0; i < segs.length; i++) { if (!host || typeof host !== 'object') return null; host = host[segs[i]]; }
    return host;
  };
  lift(mine, incoming);
  (MERGE_SPEC.project || []).forEach(spec => {
    const a = resolve(mine, spec.path), b = resolve(incoming, spec.path);
    if (spec.map) {
      if (a && b && typeof a === 'object' && typeof b === 'object')
        Object.keys(b).forEach(k => lift(a[k], b[k]));
      return;
    }
    if (!Array.isArray(a) || !Array.isArray(b)) return;
    const key = spec.key || 'id';
    b.forEach(rec => {
      if (!rec || rec[key] === undefined) return;
      lift(a.find(x => x && String(x[key]) === String(rec[key])), rec);
    });
  });
}
function _importDiffers(incoming) {
  const mine = ((window.__DB__ && window.__DB__.projects) || [])
    .find(c => c && incoming.uid && c.uid === incoming.uid);
  if (!mine) return null;                                /* no counterpart — not a collision */
  return _projectContentKey(mine) !== _projectContentKey(incoming);
}

function onImportFileChosen(e) {
  const prev = document.getElementById('importPreview');
  const btn = document.getElementById('importBtn');
  const file = e.target.files && e.target.files[0];
  if (!file) { if (prev) prev.innerHTML = ''; if (btn) btn.disabled = true; _importStaged = null; return; }
  /* ══ ⚠ v14 (W8): IMPORT IS FOR EXTERNAL FILES, AND A TEAMMATE'S PART IS NOT ONE ═══════════
     This is a correctness guard, not a tidy-up. Until v14, importing a colleague's data file was
     the SUPPORTED way to get their work — that is what the old wording offered. Now the app reads
     every part in the folder and merges them automatically, so their projects are ALREADY in the
     portfolio you are looking at.
     Importing one on top would be actively harmful: the import path mints FRESH ids for anything
     it copies, so you would end up with a second, divergent copy of each of their projects sitting
     beside the merged originals — with no relationship to them, and saved into YOUR part, where it
     would then spread to everyone else on the next merge. Nothing in the merge could undo that,
     because to the merge the copies are simply new records.
     Refused by NAME, before the file is read at all: the part pattern is the one thing that
     reliably says "this belongs to the folder's own machinery". */
  if (/^TrendAI_FlightDeck_data\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.js$/.test(String(file.name || ''))) {
    if (prev) {
      prev.innerHTML = `<div class="import-refused"><strong>That is a teammate’s v17 data file.</strong> `
        + `<code>${_escapeHtml(file.name)}</code> is one of the per-person files the old version of FlightDeck kept — `
        + `their contents were moved into the server by the one-time migration (npm run import-legacy), so importing it here would make a second, separate copy of everything in it. `
        + `Import is for exports and backups.</div>`;
    }
    if (btn) btn.disabled = true;
    _importStaged = null;
    if (e && e.target) e.target.value = '';
    return;
  }
  const reader = new FileReader();
  reader.onload = () => {
    try {
      const staged = _parseImportText(reader.result);
      const projects = staged.projects;
      const existing = new Set(((window.__DB__ && window.__DB__.projects) || []).map(c => c && c.uid).filter(Boolean));
      const rows = projects.map(c => {
        const conflict = c.uid && existing.has(c.uid);
        /* v11 (◆E): on a COLLISION, say whether it actually differs. After a fork most
           collisions are the same untouched project on both sides, and this is what
           points you at the few that are not. */
        const differs = conflict ? _importDiffers(c) : null;
        return `<div class="import-row"><span class="import-row-name">${_escapeHtml(c.name || 'Untitled project')}</span>`
          + (conflict ? `<span class="import-badge import-badge-exists">already exists</span>`
                      : `<span class="import-badge import-badge-new">new</span>`)
          + (differs === true ? `<span class="import-badge import-badge-differs" title="This project's content is not identical to yours. Import it as a copy to compare them side by side.">differs</span>` : '')
          + (differs === false ? `<span class="import-badge import-badge-same" title="Identical to your version — nothing would change.">same</span>` : '')
          + `</div>`;
      }).join('');
      const nConf = projects.filter(c => c.uid && existing.has(c.uid)).length;
      const conflictUI = nConf ?
          `<div class="import-conflict"><div class="import-conflict-q">${nConf} project${nConf > 1 ? 's' : ''} already exist here. For those:</div>`
        + `<label class="import-opt"><input type="radio" name="importConflict" value="copy" checked> Import as copies (keep both)</label>`
        + `<label class="import-opt"><input type="radio" name="importConflict" value="replace"> Replace my existing version</label>`
        + `<label class="import-opt"><input type="radio" name="importConflict" value="skip"> Skip (keep mine)</label></div>`
        : '';
      /* v10.4.3: say what the programme branch will bring, and offer it the same
         no-silent-loss choices the projects already get. */
      const progUI = _importProgrammePreviewHtml(staged.programmes);
      if (prev) prev.innerHTML = `<div class="import-found">${projects.length} project${projects.length > 1 ? 's' : ''} in this file:</div>`
        + `<div class="import-list">${rows}</div>${conflictUI}${progUI}`;
      _importStaged = { projects: projects, programmes: staged.programmes };
      if (btn) btn.disabled = false;
    } catch (err) {
      if (prev) prev.innerHTML = `<div class="import-error">${_escapeHtml(err && err.message || 'Could not read that file.')}</div>`;
      _importStaged = null;
      if (btn) btn.disabled = true;
    }
  };
  reader.onerror = () => { if (prev) prev.innerHTML = `<div class="import-error">Could not read that file.</div>`; if (btn) btn.disabled = true; _importStaged = null; };
  reader.readAsText(file);
}

function confirmImport() {
  if (!_importStaged || !_importStaged.projects.length) return;
  const db = window.__DB__; if (!db || !Array.isArray(db.projects)) return;
  const mode = (document.querySelector('input[name="importConflict"]:checked') || {}).value || 'copy';
  let added = 0, replaced = 0, copied = 0, skipped = 0;
  /* ── v10.4.3: incoming project id -> the id it ended up with HERE (or the equivalent
     project we already had). Benefit->project links are references BY id, and the copy
     path mints a fresh id, so without this map every imported benefit would point at
     nothing — and v10.0 makes a dangling reference drop SILENTLY at render, so the links
     would simply vanish with no error at all. This is the part of v10.4.3 most likely to
     be got wrong, which is why it has its own harness check. */
  /* v11: STEP 1 — programmes first, so a project's programmeId can be remapped as it
     arrives. Their benefits wait for step 3, which needs the project map below. */
  const _progPass = _applyImportedProgrammes();
  const idMap = Object.create(null);
  /* v17.1 W3: pre-plan fresh UIDs for the whole copy batch before copying its first
     project. A dependent may appear before its predecessor, so remapping on the fly
     cannot know the later endpoint yet. References outside the copied batch stay
     untouched and therefore remain explicit unavailable UIDs rather than disappearing. */
  const copyUidMap = Object.create(null);
  if (mode === 'copy') {
    _importStaged.projects.forEach(function (raw) {
      const uid = raw && typeof raw.uid === 'string' ? raw.uid : '';
      if (uid && db.projects.some(function (p) { return p && p.uid === uid; }))
        copyUidMap[uid] = _newUid();
    });
  }
  let _replacedActive = false;   /* v15.2 — see the re-point below */
  _importStaged.projects.forEach(raw => {
    const fromId = (typeof raw.id === 'string' && raw.id) ? raw.id : null;
    const c = JSON.parse(JSON.stringify(raw));   // deep clone, data-only (no shared refs)
    /* defensive defaults so a partial/older file still renders + activates cleanly */
    /* ⚠ v13.1 (A14): `assumptions` was missing from this list, and it is a TOP-LEVEL
       serialised field — so an imported project without one kept `undefined`, and the
       serialiser then wrote the bare token `undefined` into the data file. This list must
       name every array field the serialiser emits; the guard in serializeDB is the second
       line of defence, not a substitute for this one. */
    ['actions', 'sessions', 'stakeholders', 'risks', 'assumptions', 'workstreams', 'newActions'].forEach(k => { if (!Array.isArray(c[k])) c[k] = []; });
    if (typeof c.edits !== 'object' || c.edits === null) c.edits = {};
    if (typeof c.appMeta !== 'object' || c.appMeta === null) c.appMeta = (typeof _blankAppMeta === 'function' ? _blankAppMeta() : {});
    if (typeof c.archived !== 'boolean') c.archived = false;
    if (!c.name) c.name = 'Imported project';
    /* v7: uid is the identity/match key. Guarantee the incoming record has one (a
       hand-edited or pre-v7 file may not) so "every project has a uid" always holds.
       Matching is on uid ONLY — there is no slug fallback. */
    if (typeof c.uid !== 'string' || !c.uid) c.uid = _newUid();
    const fromUid = c.uid;
    /* v11: point the project at the programme it ended up in HERE. Without this it
       would keep the SOURCE file's programmeId, name a programme that may not exist,
       and _normProgrammes would silently adopt it into the first one. */
    const _srcProg = (typeof c.programmeId === 'string') ? c.programmeId : '';
    c.programmeId = _progPass.progIdMap[_srcProg] || activeProgrammeId || '';
    const idx = db.projects.findIndex(x => x && x.uid === c.uid);
    if (idx === -1) {
      /* new project: a uid-match miss does NOT imply a slug-match miss, so make the
         readable slug unique too — otherwise two accounts could share a deep-link id. */
      if (!c.id || db.projects.some(x => x && x.id === c.id)) c.id = _uniqueProjectId(c.name);
      db.projects.push(c); added++;
    } else if (mode === 'replace') {
      c.id = db.projects[idx].id;             // keep the existing slug so deep-links stay valid
      _importCarryRevs(db.projects[idx], c);  /* v15.2: an older backup must not lose the next merge */
      db.projects[idx] = c; replaced++;
      /* ⚠ v15.2: replacing the ACTIVE project leaves ACTIONS/edits/newActions/deletedIds
         pointing at the ORPHANED pre-import object (activeProjectId is sticky and only
         setActiveProject re-points). The next save then baked the OLD actions over the
         imported record — chimera bytes, silently. Re-pointed once, after the loop. */
      if (c.id === activeProjectId) _replacedActive = true;
    } else if (mode === 'skip') {
      skipped++;
      /* v10.4.3: we kept OUR copy, so an incoming benefit that referenced this project
         should resolve to the one we already have rather than being dropped. */
      if (fromId) idMap[fromId] = db.projects[idx].id;
      return;
    } else {                                   // copy: a NEW distinct project — fresh slug AND fresh uid
      c.name = c.name + ' (imported)';
      c.id = _uniqueProjectId(c.name);
      c.uid = copyUidMap[fromUid] || _newUid();
      if (c.appMeta && Array.isArray(c.appMeta.predecessorUids)) {
        c.appMeta.predecessorUids = c.appMeta.predecessorUids.map(function (uid) {
          return copyUidMap[uid] || uid;
        });
      }
      db.projects.push(c); copied++;
    }
    if (fromId) idMap[fromId] = c.id;
  });
  const prog = _applyImportedBenefits(idMap, _progPass.progIdMap);
  prog.added = _progPass.added;
  if (_replacedActive && typeof setActiveProject === 'function') setActiveProject(activeProjectId);
  if (typeof markDirty === 'function') markDirty();
  if (typeof renderOverview === 'function') renderOverview();
  /* v15.2: Import is reachable only FROM the portfolio, whose list/counts/CI pane were the
     one view NOT repainted — the toast said "Imported: 2 added" over a screen showing nothing
     new until navigation. */
  if (typeof renderPortfolio === 'function') renderPortfolio();
  closeImport();
  const parts = [];
  if (added) parts.push(`${added} added`);
  if (replaced) parts.push(`${replaced} replaced`);
  if (copied) parts.push(`${copied} as ${copied > 1 ? 'copies' : 'a copy'}`);
  if (skipped) parts.push(`${skipped} skipped`);
  if (prog.benefits) parts.push(`${prog.benefits} benefit${prog.benefits > 1 ? 's' : ''}`);
  if (prog.rag) parts.push('programme status');
  if (typeof showToast === 'function') showToast('Imported: ' + (parts.join(', ') || 'nothing'));
}
/* ── v10.4.3: bring the programme branch across ────────────────────────────────────────
   Same posture the project import already has: no silent overwrite, copies by default.
     benefits  copy (default) -> appended with FRESH BEN-### numbers off the live benSeq
               replace        -> the register becomes the incoming one, benSeq raised so a
                                 later add cannot reuse an imported number
               skip           -> nothing
     RAG       default KEEP MINE, opt in to take theirs. A RAG level is a human judgement
               (v10.0); overwriting it unasked would be the same category of error as the
               export omission this version exists to fix, only quieter.
   `idMap` comes from the project pass; every incoming projectIds entry is remapped through
   it and anything unmapped is dropped, so no imported benefit is left pointing at nothing. */
function _applyImportedProgrammes() {
  /* STEP 1 of 3. Locate or create each incoming programme and return the map from its id
     in the source file to the id it has HERE. No benefits are touched yet — they need the
     project map, which does not exist until step 2. */
  const out = { added: 0, matched: 0, progIdMap: Object.create(null) };
  const inc = (_importStaged && _importStaged.programmes) || [];
  const db = window.__DB__;
  if (!inc.length || !db) return out;
  if (!Array.isArray(db.programmes)) db.programmes = [];
  inc.forEach(src => {
    /* Match on id first, then on a non-empty name — the same "identity, then the
       human-readable thing" order the project pass uses with uid then slug. */
    let mine = src.id ? db.programmes.find(P => P && P.id === src.id) : null;
    if (!mine && src.name && src.name.trim()) {
      const want = src.name.trim().toLowerCase();
      mine = db.programmes.find(P => P && typeof P.name === 'string' && P.name.trim().toLowerCase() === want);
    }
    if (mine) {
      out.matched++;
      if (src.id) out.progIdMap[src.id] = mine.id;
      return;
    }
    const id = createProgramme(src.name || '');
    const rec = db.programmes.find(P => P && P.id === id);
    /* ── v13.1 (A15), the applying half ────────────────────────────────────────────────
       Carrying the extra keys through the parser achieves nothing unless they are written
       onto the record, and this function only ever copied `rag`. Applied ONLY to a
       programme we have just CREATED — a matched existing one belongs to the user and is
       never overwritten, which is why the match branch returns above. `id` and `name` are
       owned by createProgramme, `rag` is handled below, and `benefits` are step 3's job.
       An absent key stays absent, so `manager` and `archived` keep their laziness and a
       programme that never had them still round-trips byte-identically. */
    if (rec) {
      Object.keys(src).forEach(k => {
        if (k === 'id' || k === 'name' || k === 'rag' || k === 'benefits') return;
        /* v15.2: merge bookkeeping is NOT content — copying the source's rev/writer onto a
           programme just CREATED HERE claims their authorship of my record, and a copied
           `del` resurrected ghost programmes before the parse-level harvest existed. */
        if (k === 'rev' || k === 'writer' || k === 'del') return;
        if (src[k] === undefined) return;
        rec[k] = src[k];
      });
    }
    if (rec && (src.rag.level !== null || src.rag.rationale)) {
      /* A programme arriving whole brings its own status: there is no existing human
         judgement here to overwrite, so nothing is being taken from anyone. */
      rec.rag.level = src.rag.level;
      rec.rag.rationale = src.rag.rationale;
    }
    out.added++;
    if (src.id) out.progIdMap[src.id] = id;
  });
  return out;
}
/* ── STEP 3 of 3: the benefits (and the RAG opt-in for programmes that already existed) ─
   Unchanged in posture from v10.4.3, now applied PER PROGRAMME:
     benefits  copy (default) / replace / skip
     RAG       default KEEP MINE, opt in to take theirs — a RAG level is a human
               judgement (v10.0), and overwriting it unasked is the same category of
               error as the export omission v10.4.3 existed to fix, only quieter.
   ⚠ `idMap` comes from the project pass and `progIdMap` from step 1. Every incoming
   projectIds entry is remapped through idMap and anything unmapped is DROPPED, so no
   imported benefit is left pointing at nothing — v10.0 drops a dangling reference
   silently at render, so getting this wrong loses links with no error anywhere. */
function _applyImportedBenefits(idMap, progIdMap) {
  const out = { benefits: 0, rag: false };
  const inc = (_importStaged && _importStaged.programmes) || [];
  const db = window.__DB__;
  if (!inc.length || !db || !Array.isArray(db.programmes)) return out;
  const takeRag = !!(document.getElementById('importTakeRag') || {}).checked;
  const benMode = (document.querySelector('input[name="importBenefits"]:checked') || {}).value || 'copy';

  const bring = (raw) => {
    const b = JSON.parse(JSON.stringify(raw));
    _normBenefit(b);
    const src = Array.isArray(b.projectIds) ? b.projectIds : [];
    b.projectIds = src.map(pid => idMap[pid]).filter(x => typeof x === 'string' && x);
    return b;
  };

  inc.forEach(srcProg => {
    const destId = srcProg.id ? progIdMap[srcProg.id] : null;
    const P = destId ? db.programmes.find(x => x && x.id === destId) : null;
    if (!P) return;
    if (!P.rag || typeof P.rag !== 'object') P.rag = { level: null, rationale: '' };
    if (!Array.isArray(P.benefits)) P.benefits = [];

    if (takeRag && (srcProg.rag.level !== null || srcProg.rag.rationale)) {
      P.rag.level = srcProg.rag.level;
      P.rag.rationale = srcProg.rag.rationale;
      out.rag = true;
    }
    if (benMode === 'skip' || !srcProg.benefits.length) return;

    if (benMode === 'replace') {
      P.benefits.length = 0;
      srcProg.benefits.forEach(raw => P.benefits.push(bring(raw)));
      /* ⚠ v15.2: the high-water mark rises only for ids inside MY OWN mint block.
         `_mintNextId` honours a stored seq only in-block (deliberate), so raising benSeq to a
         colleague's six-figure block id OVERWROTE my in-block memory with a number the minter
         ignores — and with the live register just replaced (tombstones invisible to the
         minter), the next add re-issued an id already used by a replaced-away benefit, which
         a colleague's stale tombstone could then silently delete at their next merge. */
      const _myBlock = _mintBlock(_myWriterId());
      const maxMine = P.benefits.reduce((m, b) => {
        const n = Number(b.id) || 0;
        return (n > _myBlock && n < _myBlock + MINT_BLOCK_SIZE) ? Math.max(m, n) : m;
      }, 0);
      P.benSeq = Math.max(Number(P.benSeq) || 0, maxMine);
      out.benefits += P.benefits.length;
      return;
    }
    /* copy: a fresh number off THIS programme's sequence, so an imported benefit never
       collides with one of mine. _nextBenSeq reads the active programme, so aim it. */
    const keepActive = activeProgrammeId;
    activeProgrammeId = P.id;
    try {
      srcProg.benefits.forEach(raw => {
        const b = bring(raw);
        b.id = _nextBenSeq();
        P.benefits.push(b);
        out.benefits++;
      });
    } finally { activeProgrammeId = keepActive; }
  });
  return out;
}
/* The programme part of the import preview. Empty string when the file carries nothing at
   programme level (a pre-v10.0 export), so the dialog is unchanged in that case.
   v11: takes the ARRAY. It names the programmes coming across — under one file a fork
   carries every one of them, so "which programmes am I about to take?" is now a real
   question the user needs answered before pressing the button. */
function _importProgrammePreviewHtml(incList) {
  const list = Array.isArray(incList) ? incList : (incList ? [incList] : []);
  if (!list.length) return '';
  const nBen = list.reduce((n, P) => n + (P.benefits ? P.benefits.length : 0), 0);
  const hasRag = list.some(P => P.rag && (P.rag.level !== null || !!P.rag.rationale));
  const names = list.map(P => (P.name && P.name.trim()) ? P.name.trim() : 'Unnamed programme');
  const bits = [];
  bits.push(list.length + ' programme' + (list.length > 1 ? 's' : '')
            + ' (' + names.slice(0, 4).join(', ') + (names.length > 4 ? '…' : '') + ')');
  if (nBen) bits.push(nBen + ' programme benefit' + (nBen > 1 ? 's' : ''));
  if (hasRag) bits.push('programme status');
  let html = `<div class="import-conflict"><div class="import-conflict-q">This file also carries ${_escapeHtml(bits.join(' · '))}. Programmes you do not already have are added; ones you do are matched by name.</div>`;
  if (nBen) {
    html += `<label class="import-opt"><input type="radio" name="importBenefits" value="copy" checked> Add the benefits alongside mine (fresh BEN numbers)</label>`
          + `<label class="import-opt"><input type="radio" name="importBenefits" value="replace"> Replace my benefits register</label>`
          + `<label class="import-opt"><input type="radio" name="importBenefits" value="skip"> Skip the benefits</label>`;
  }
  if (hasRag) {
    html += `<label class="import-opt"><input type="checkbox" id="importTakeRag"> Also take the programme status and rationale (otherwise mine is kept)</label>`;
  }
  return html + `</div>`;
}

function toggleProjSwitcher(e) {
  if (e) e.stopPropagation();
  if (document.querySelector('.crumb-proj .ov-rename-input')) return;   // ignore while renaming
  const dd = document.getElementById('projSwitcher');
  if (!dd) return;
  if (!dd.hidden) { dd.hidden = true; return; }
  const db = window.__DB__ || { projects: [] };
  const items = _progProjects().map(c =>   /* v11: switch within this programme only */
    `<button class="proj-switch-item${c.id === activeProjectId ? ' current' : ''}" data-id="${_escapeHtml(c.id)}" `
    + `onclick="gotoProject(this.dataset.id); var d=document.getElementById('projSwitcher'); if(d)d.hidden=true;">`
    + `${_escapeHtml(c.name || 'Untitled project')}</button>`
  ).join('');
  dd.innerHTML = items || `<div class="ov-empty" style="padding:14px;">No projects</div>`;
  dd.hidden = false;
}
/* close the project switcher on any outside click */
document.addEventListener('click', function (e) {
  const dd = document.getElementById('projSwitcher');
  if (dd && !dd.hidden && !e.target.closest('.crumb-proj')) dd.hidden = true;
  const am = document.getElementById('projMenu');
  if (am && !am.hidden && !e.target.closest('.crumb-manage')) am.hidden = true;
  /* v10.4 (W3): the manage menu moved from the card wrapper into the project table
     row, so the "did the click land inside my own menu" test must follow it. Left
     pointing at .ov-card-wrap this would have matched nothing, and every click —
     including one ON a menu item — would have closed the menu before it acted. */
  document.querySelectorAll('.ov-card-menu').forEach(m => {
    if (!m.hidden && !e.target.closest('.pm-menu-wrap')) m.hidden = true;
  });
});

/* v5.0 boot: wire hash routing, then land on the overview (or honor a deep-link #/project/<id>). */
window.addEventListener('hashchange', route);
/* fresh-start friendly: a valid DB object with a projects ARRAY counts as a clean load
   even when empty (e.g. a blank or handover file) — so a first project can be created
   and saved. Only a missing/malformed __DB__ (a genuine parse failure) leaves _dbLoadOk
   false and blocks saving, which protects a data file that failed to load. */
if (window.__DB__ && typeof window.__DB__ === 'object' && Array.isArray(window.__DB__.projects)) {
  if (window.__DB__.schemaVersion !== SCHEMA_VERSION) {
    /* ⚠ v13.1 (A54): this comment used to say schema 8 was BOTH required AND rejected —
       "schema 8 is required" and then "schema 8 and below are cleanly rejected" in the same
       breath. It was written at v10.0, when 8 genuinely was the requirement, and never
       updated when v11 moved to 9. The truth, and it matches SCHEMA_VERSION above:
       SCHEMA 9 IS REQUIRED; 8 and everything below it are cleanly rejected. v11 made the
       `programmes` branch plural and the file the whole portfolio, which is the break 9
       marks. There is no migration path by design (the 2026-07-23 back-compat window), so
       refusing IS the gate working — we do NOT surface a rejected file's data. Saving stays
       blocked (_dbLoadOk false), so the file on disk is never touched; the overview shows
       the schema-mismatch banner instead. */
    _dbLoadOk = false;
    window.__DB__.projects = [];   // in-memory only; nothing stale is shown or editable
  } else {
    _dbLoadOk = true;
    /* v10.3 (W2): remember the file's savedAt as this tab's freshness baseline, so a
       second tab cannot silently overwrite this session (see _writeDataFile). */
    _dataFileSavedAt = (typeof window.__DB__.savedAt === 'string' && window.__DB__.savedAt) ? window.__DB__.savedAt : null;
    /* v5.2.3: heal stakeholder ids across every project at load — see _normalizeStakeholderIds.
       Silent (no dirty flip, matching activation semantics): in-memory data is valid
       immediately, and the repair is written out on the next Save (any save serialises the
       whole healed DB). The shipped data file is delivered already healed. */
    window.__DB__.projects.forEach(c => { if (c) _normalizeStakeholderIds(c.stakeholders); });
    /* v7: idempotent uid backfill. The shipped data file is delivered already migrated, so
       this is a no-op on the normal path; if it does stamp (an un-migrated file), the app
       goes dirty so the freshly minted uids are written on the next Save. */
    if (typeof _backfillUids === 'function' && _backfillUids(window.__DB__) > 0 && typeof markDirty === 'function') markDirty();
    /* ── v14 (W4a): the change-counter baseline, captured LAST ────────────────────────────
       After the stakeholder heal and the uid backfill deliberately: both are idempotent
       normalisations that every writer's shell performs identically, so treating them as
       CHANGES would bump every record's rev on a boot that altered nothing, and a save that
       bumps everything beats every colleague for no reason. Capturing here also strips the
       tombstones out of the live arrays before a single renderer sees them. */
    _revAdoptBaseline(window.__DB__);
  }
}
/* ── v11: reopen the programme you last looked at ─────────────────────────────────────
   Per USER (a server preference), not stored in the shared portfolio (see _saveLastProgramme). This only
   ever REFINES the choice: _normProgrammes has already picked programmes[0] as a safe
   default, and an id naming a programme that no longer exists is ignored rather than
   leaving nothing active. Fire-and-forget and fully guarded, like the handle restore. */
/* ⚠ v14 (W4b): WRAPPED IN A FUNCTION AND MOVED BEHIND THE MERGE. This chain used to run at module
   level, which raced the parts load it now follows: it resolves from IndexedDB in a millisecond or
   two, so it reached `_programmes()` while the portfolio was still the empty seed, found the
   remembered programme absent, and correctly did nothing — and then the merge landed and routed,
   losing the resume every time. _bootFromServer awaits it after the adopt. */
function _restoreLastProgrammeAndView() {
  return (typeof _loadLastProgramme === 'function' ? _loadLastProgramme() : Promise.resolve(null))
  .then(id => {
    if (!id || id === activeProgrammeId) return;
    if (!_programmes().some(p => p && p.id === id)) return;
    activeProgrammeId = id;
    if (document.body.classList.contains('view-overview') && typeof renderOverview === 'function') renderOverview();
    if (document.body.classList.contains('view-portfolio') && typeof renderPortfolio === 'function') renderPortfolio();
    if (typeof _renderProgCrumb === 'function') _renderProgCrumb();
  })
  .catch(() => {})
  /* v12.1 (W2, ◆E): resume where he last SAVED from — CHAINED after the programme restore,
     so the overview can never paint before activeProgrammeId has settled. A hash already in
     the URL is a deep link and always wins; an archived (or missing) programme falls back to
     the portfolio route() already put us on. */
  .then(() => (typeof _loadLastView === 'function' ? _loadLastView() : null))
  .then(h => {
    if (typeof h !== 'string' || !h || h === '#/portfolio') return;
    if (location.hash) return;
    if (h === '#/overview' || h.indexOf('#/project/') === 0) {
      const P = _activeProg();
      if (!P || P.archived) return;
    }
    location.hash = h;
  })
  .catch(() => {});
}


